import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import LazyImage from '../../../components/LazyImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useAuth } from '../../../contexts/AuthContext';
import { getUserProfile, updatePersonalInfo, updateProfilePhoto } from '../../../services/profileService';

const PersonalInfoSection = () => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState({
    fullName: "",
    email: "",
    phone: "",
    dateOfBirth: "",
    gender: "female",
    height: "",
    weight: "",
    profilePhoto: ""
  });
  const [tempData, setTempData] = useState(profileData);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    loadProfile();
  }, [user]);

  const loadProfile = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const profile = await getUserProfile(user?.id);
      
      if (profile) {
        const loadedData = {
          fullName: profile?.fullName || "",
          email: profile?.email || "",
          phone: profile?.phone || "",
          dateOfBirth: profile?.dateOfBirth || "",
          gender: profile?.gender || "female",
          height: profile?.height?.toString() || "",
          weight: profile?.weight?.toString() || "",
          profilePhoto: profile?.avatarUrl || ""
        };
        setProfileData(loadedData);
        setTempData(loadedData);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setTempData(profileData);
    setErrors({});
  };

  const handleCancel = () => {
    setIsEditing(false);
    setTempData(profileData);
    setErrors({});
  };

  const handleSave = async () => {
    const newErrors = {};
    
    if (!tempData?.fullName?.trim()) {
      newErrors.fullName = "Full name is required";
    }
    
    if (!tempData?.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/?.test(tempData?.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setSaving(true);
      const result = await updatePersonalInfo(user?.id, {
        fullName: tempData?.fullName,
        phone: tempData?.phone,
        dateOfBirth: tempData?.dateOfBirth,
        gender: tempData?.gender,
        height: tempData?.height ? parseInt(tempData?.height) : null,
        weight: tempData?.weight ? parseFloat(tempData?.weight) : null
      });

      if (result?.success) {
        setProfileData(tempData);
        setIsEditing(false);
        setErrors({});
      } else {
        setErrors({ general: result?.error || 'Failed to save changes' });
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      setErrors({ general: 'Failed to save changes. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setTempData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file?.type?.startsWith('image/')) {
      setErrors({ photo: 'Please select an image file' });
      return;
    }

    // Validate file size (max 5MB)
    if (file?.size > 5 * 1024 * 1024) {
      setErrors({ photo: 'Image size must be less than 5MB' });
      return;
    }

    try {
      setUploadingPhoto(true);
      
      // Preview image immediately
      const reader = new FileReader();
      reader.onload = (event) => {
        setTempData(prev => ({ ...prev, profilePhoto: event?.target?.result }));
      };
      reader?.readAsDataURL(file);

      // Upload to Supabase
      const result = await updateProfilePhoto(user?.id, file);
      
      if (result?.success) {
        setProfileData(prev => ({ ...prev, profilePhoto: result?.avatarUrl }));
        setTempData(prev => ({ ...prev, profilePhoto: result?.avatarUrl }));
      } else {
        setErrors({ photo: result?.error || 'Failed to upload photo' });
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      setErrors({ photo: 'Failed to upload photo. Please try again.' });
    } finally {
      setUploadingPhoto(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-xl p-6 elevation-2">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl p-6 elevation-2">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-lg">
            <Icon name="User" size={20} color="white" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Personal Information</h2>
            <p className="text-sm text-muted-foreground">Manage your account details and profile</p>
          </div>
        </div>
        {!isEditing ? (
          <Button variant="outline" onClick={handleEdit} iconName="Edit" iconPosition="left">
            Edit
          </Button>
        ) : (
          <div className="flex space-x-2">
            <Button variant="outline" onClick={handleCancel} size="sm" disabled={saving}>
              Cancel
            </Button>
            <Button variant="default" onClick={handleSave} size="sm" disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        )}
      </div>
      {errors?.general && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
          {errors?.general}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Photo */}
        <div className="lg:col-span-1">
          <div className="text-center">
            <div className="relative inline-block">
              <div className="w-32 h-32 rounded-full overflow-hidden bg-muted">
                {(isEditing ? tempData?.profilePhoto : profileData?.profilePhoto) ? (
                  <LazyImage
                    src={isEditing ? tempData?.profilePhoto : profileData?.profilePhoto}
                    alt="Profile photo"
                    className="w-full h-full"
                    sizes="128px"
                    eager={true}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-primary/10">
                    <Icon name="User" size={48} className="text-primary" />
                  </div>
                )}
              </div>
              {isEditing && (
                <label className="absolute bottom-0 right-0 w-10 h-10 bg-primary rounded-full flex items-center justify-center cursor-pointer hover:bg-primary/90 transition-colors">
                  {uploadingPhoto ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <Icon name="Camera" size={16} color="white" />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    disabled={uploadingPhoto}
                  />
                </label>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-3">
              {isEditing ? "Click camera icon to change photo" : "Profile Photo"}
            </p>
            {errors?.photo && (
              <p className="text-xs text-destructive mt-1">{errors?.photo}</p>
            )}
          </div>
        </div>

        {/* Personal Details */}
        <div className="lg:col-span-2 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Full Name"
              type="text"
              name="fullName"
              value={isEditing ? tempData?.fullName : profileData?.fullName}
              onChange={handleInputChange}
              disabled={!isEditing}
              error={errors?.fullName}
              required
            />
            <Input
              label="Email Address"
              type="email"
              name="email"
              value={isEditing ? tempData?.email : profileData?.email}
              onChange={handleInputChange}
              disabled
              error={errors?.email}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Phone Number"
              type="tel"
              name="phone"
              value={isEditing ? tempData?.phone : profileData?.phone}
              onChange={handleInputChange}
              disabled={!isEditing}
            />
            <Input
              label="Date of Birth"
              type="date"
              name="dateOfBirth"
              value={isEditing ? tempData?.dateOfBirth : profileData?.dateOfBirth}
              onChange={handleInputChange}
              disabled={!isEditing}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Gender</label>
              <select
                name="gender"
                value={isEditing ? tempData?.gender : profileData?.gender}
                onChange={handleInputChange}
                disabled={!isEditing}
                className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground disabled:opacity-50 disabled:cursor-not-allowed focus:ring-2 focus:ring-ring focus:border-transparent"
              >
                <option value="female">Female</option>
                <option value="male">Male</option>
                <option value="other">Other</option>
                <option value="prefer-not-to-say">Prefer not to say</option>
              </select>
            </div>
            <Input
              label="Height (cm)"
              type="number"
              name="height"
              value={isEditing ? tempData?.height : profileData?.height}
              onChange={handleInputChange}
              disabled={!isEditing}
              min="100"
              max="250"
            />
            <Input
              label="Weight (kg)"
              type="number"
              name="weight"
              value={isEditing ? tempData?.weight : profileData?.weight}
              onChange={handleInputChange}
              disabled={!isEditing}
              min="30"
              max="200"
              step="0.1"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalInfoSection;